<template>
  <div class="mcl-el-page">
    <header class="mcl-el-hero">
      <div class="mcl-el-hero__main">
        <div class="mcl-el-hero__icon-wrap">
          <font-awesome-icon :icon="['fas', 'envelope-open-text']" class="mcl-el-hero__icon" />
        </div>
        <div class="mcl-el-hero__text">
          <h2 class="mcl-el-hero__title">Email Logs</h2>
          <p class="mcl-el-hero__subtitle">Track outbound messages sent from Master Contact List</p>
        </div>
      </div>
      <div v-if="paginatorInfo && paginatorInfo.total > 0" class="mcl-el-hero__stats">
        <div class="mcl-el-stat-chip">
          <span class="mcl-el-stat-chip__value">{{ paginatorInfo.total }}</span>
          <span class="mcl-el-stat-chip__label">Total emails</span>
        </div>
        <div class="mcl-el-stat-chip mcl-el-stat-chip--page">
          <span class="mcl-el-stat-chip__value">{{ paginatorInfo.currentPage }}</span>
          <span class="mcl-el-stat-chip__label">of {{ paginatorInfo.lastPage }} pages</span>
        </div>
      </div>
    </header>

    <nav class="mcl-el-tabs" aria-label="Email log sections">
      <button
        type="button"
        class="mcl-el-tabs__btn"
        :class="{ 'mcl-el-tabs__btn--active': activeFolder === 'sent' }"
        @click.stop="switchFolder('sent')"
      >
        <font-awesome-icon :icon="['fas', 'paper-plane']" />
        <span>Sent</span>
        <span v-if="logCounts" class="mcl-el-tabs__badge">{{ logCounts.sent_count }}</span>
      </button>
      <button
        type="button"
        class="mcl-el-tabs__btn"
        :class="{ 'mcl-el-tabs__btn--active': activeFolder === 'scheduled' }"
        @click.stop="switchFolder('scheduled')"
      >
        <font-awesome-icon :icon="['fas', 'clock']" />
        <span>Scheduled</span>
        <span v-if="logCounts" class="mcl-el-tabs__badge mcl-el-tabs__badge--scheduled">{{ logCounts.scheduled_count }}</span>
      </button>
    </nav>

    <div v-if="activeFolder === 'scheduled'" class="mcl-el-scheduled-banner">
      <font-awesome-icon :icon="['fas', 'clock']" />
      <span>Messages in Scheduled will be sent at their scheduled time.</span>
    </div>

    <div class="mcl-el-toolbar-card">
      <div class="mcl-el-search">
        <font-awesome-icon :icon="['fas', 'search']" class="mcl-el-search__icon" />
        <input
          v-model="search"
          type="text"
          placeholder="Search subject, sender, or recipient..."
          class="mcl-el-search__input"
          @keyup.enter="onSearch"
        />
        <button type="button" class="mcl-el-search__btn" @click.stop="onSearch">Search</button>
      </div>
      <button type="button" class="mcl-el-refresh-btn" :disabled="isLoading" @click.stop="refreshLogs">
        <font-awesome-icon :icon="['fas', isLoading ? 'spinner' : 'sync-alt']" :spin="isLoading" />
        <span>Refresh</span>
      </button>
    </div>

    <div v-if="isLoading" class="mcl-el-state mcl-el-state--loading">
      <div class="mcl-el-loader">
        <div class="mcl-el-loader__ring"></div>
        <font-awesome-icon :icon="['fas', 'envelope']" class="mcl-el-loader__icon" />
      </div>
      <p class="mcl-el-state__title">Loading email history</p>
      <p class="mcl-el-state__text">{{ loadingMessage }}</p>
    </div>

    <div v-else-if="emailLogs.length === 0" class="mcl-el-state mcl-el-state--empty">
      <div class="mcl-el-empty-icon">
        <font-awesome-icon :icon="['fas', 'inbox']" />
      </div>
      <h3 class="mcl-el-state__title">{{ emptyTitle }}</h3>
      <p class="mcl-el-state__text">{{ emptyMessage }}</p>
    </div>

    <div v-else class="mcl-el-content">
      <div class="mcl-el-table-wrap">
        <table class="mcl-el-table">
          <thead>
            <tr>
              <th class="mcl-el-table__col-num">#</th>
              <th>Recipients</th>
              <th>Sent by</th>
              <th>Subject</th>
              <th>Status</th>
              <th>{{ dateColumnLabel }}</th>
              <th class="mcl-el-table__col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(log, index) in emailLogs" :key="log.id">
              <tr
                class="mcl-el-table__row"
                :class="{ 'mcl-el-table__row--expanded': isExpanded(log) }"
              >
                <td class="mcl-el-table__col-num">
                  <span class="mcl-el-row-num">{{ rowNumber(index) }}</span>
                </td>
                <td class="mcl-el-table__recipients">
                  <button
                    type="button"
                    class="mcl-el-recipient-btn"
                    @click.stop="toggleExpanded(log)"
                  >
                    <template v-if="hasSingleRecipient(log)">
                      <span class="mcl-el-recipient-line">
                        <span class="mcl-el-recipient-line__name">{{ singleRecipientNamePart(log) }}</span>
                        <span v-if="singleRecipientEmailPart(log)" class="mcl-el-recipient-line__email">
                          ({{ singleRecipientEmailPart(log) }})
                        </span>
                      </span>
                    </template>
                    <template v-else>
                      <span class="mcl-el-recipient-pill">
                        <font-awesome-icon :icon="['fas', 'users']" />
                        {{ recipientSummary(log) }}
                      </span>
                    </template>
                  </button>
                </td>
                <td class="mcl-el-table__sender">
                  <div class="mcl-el-sender">
                    <span class="mcl-el-sender__avatar">{{ senderInitials(log) }}</span>
                    <span class="mcl-el-sender__name">{{ senderLabel(log) }}</span>
                  </div>
                </td>
                <td class="mcl-el-table__subject">
                  <div class="mcl-el-subject-cell">
                    <span class="mcl-el-subject-cell__text">{{ log.subject || '—' }}</span>
                    <span v-if="log.has_attachments" class="mcl-el-subject-cell__attach" title="Has attachments">
                      <font-awesome-icon :icon="['fas', 'paperclip']" />
                    </span>
                  </div>
                </td>
                <td>
                  <span class="mcl-el-status" :class="statusClass(log.status, log)">
                    <span class="mcl-el-status__dot"></span>
                    {{ statusLabel(log.status, log) }}
                  </span>
                </td>
                <td class="mcl-el-table__date" :class="{ 'mcl-el-table__date--scheduled': isScheduledLog(log) }">
                  <span class="mcl-el-date__primary">{{ displayDatePrimary(log) }}</span>
                  <span class="mcl-el-date__secondary">{{ displayDateSecondary(log) }}</span>
                </td>
                <td class="mcl-el-table__col-actions">
                  <div class="mcl-el-row-actions">
                    <button
                      type="button"
                      class="mcl-el-resend-btn"
                      title="Resend email"
                      @click.stop="resendEmail(log)"
                    >
                      <font-awesome-icon :icon="['fas', 'paper-plane']" />
                      <span>Resend</span>
                    </button>
                    <button
                      type="button"
                      class="mcl-el-expand-btn"
                      :class="{ 'mcl-el-expand-btn--open': isExpanded(log) }"
                      :title="isExpanded(log) ? 'Hide details' : 'Show details'"
                      @click.stop="toggleExpanded(log)"
                    >
                      <font-awesome-icon :icon="['fas', 'chevron-down']" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="isExpanded(log)" class="mcl-el-detail-row">
                <td colspan="7">
                  <div class="mcl-el-detail">
                    <div class="mcl-el-detail__toolbar">
                      <button type="button" class="mcl-el-detail-resend-btn" @click.stop="resendEmail(log)">
                        <font-awesome-icon :icon="['fas', 'paper-plane']" />
                        <span>Resend email</span>
                      </button>
                      <p class="mcl-el-detail__toolbar-hint">Opens compose with recipients, subject, and body pre-filled for editing.</p>
                    </div>
                    <div class="mcl-el-detail__grid">
                      <section class="mcl-el-detail__panel mcl-el-detail__panel--recipients">
                        <div class="mcl-el-detail__panel-head">
                          <span class="mcl-el-detail__panel-icon mcl-el-detail__panel-icon--blue">
                            <font-awesome-icon :icon="['fas', 'users']" />
                          </span>
                          <div>
                            <h4 class="mcl-el-detail__panel-title">Recipients</h4>
                            <p class="mcl-el-detail__panel-desc">{{ recipientCount(log) }} total</p>
                          </div>
                        </div>
                        <div v-if="!log.recipients || log.recipients.length === 0" class="mcl-el-detail__empty">
                          No recipients recorded
                        </div>
                        <div v-else class="mcl-el-recipient-list">
                          <div
                            v-for="recipient in log.recipients"
                            :key="recipient.id"
                            class="mcl-el-recipient-item"
                          >
                            <div class="mcl-el-recipient-item__top">
                              <span class="mcl-el-recipient-item__type">{{ recipientTypeLabel(recipient.recipient_type) }}</span>
                              <span class="mcl-el-status mcl-el-status--sm" :class="deliveryStatusClass(recipient.delivery_status)">
                                <span class="mcl-el-status__dot"></span>
                                {{ deliveryStatusLabel(recipient.delivery_status) }}
                              </span>
                            </div>
                            <p class="mcl-el-recipient-item__identity">
                              <span class="mcl-el-recipient-item__name">{{ recipient.name || recipient.email }}</span>
                              <span v-if="recipient.name" class="mcl-el-recipient-item__email">{{ recipient.email }}</span>
                            </p>
                            <p v-if="recipient.delivered_at" class="mcl-el-recipient-item__time">
                              <font-awesome-icon :icon="['fas', 'clock']" />
                              {{ formatDate(recipient.delivered_at) }}
                            </p>
                            <p v-if="recipient.delivery_error" class="mcl-el-recipient-item__error">
                              {{ recipient.delivery_error }}
                            </p>
                          </div>
                        </div>
                      </section>

                      <section class="mcl-el-detail__panel mcl-el-detail__panel--info">
                        <div class="mcl-el-detail__panel-head">
                          <span class="mcl-el-detail__panel-icon mcl-el-detail__panel-icon--slate">
                            <font-awesome-icon :icon="['fas', 'info-circle']" />
                          </span>
                          <div>
                            <h4 class="mcl-el-detail__panel-title">Message info</h4>
                            <p class="mcl-el-detail__panel-desc">Delivery metadata</p>
                          </div>
                        </div>
                        <dl class="mcl-el-meta-list">
                          <div class="mcl-el-meta-list__item">
                            <dt>From</dt>
                            <dd>{{ fromLabel(log) }}</dd>
                          </div>
                          <div class="mcl-el-meta-list__item">
                            <dt>Created</dt>
                            <dd>{{ formatDate(log.created_at) }}</dd>
                          </div>
                          <div v-if="log.sent_at" class="mcl-el-meta-list__item">
                            <dt>Sent</dt>
                            <dd>{{ formatDate(log.sent_at) }}</dd>
                          </div>
                          <div v-if="log.failed_at" class="mcl-el-meta-list__item">
                            <dt>Failed</dt>
                            <dd>{{ formatDate(log.failed_at) }}</dd>
                          </div>
                          <div v-if="log.last_error" class="mcl-el-meta-list__item mcl-el-meta-list__item--error">
                            <dt>Error</dt>
                            <dd>{{ log.last_error }}</dd>
                          </div>
                        </dl>

                        <div v-if="log.attachments && log.attachments.length > 0" class="mcl-el-attachments">
                          <h5 class="mcl-el-attachments__title">
                            <font-awesome-icon :icon="['fas', 'paperclip']" />
                            Attachments
                          </h5>
                          <ul class="mcl-el-attachments__list">
                            <li v-for="attachment in log.attachments" :key="attachment.id" class="mcl-el-attachment-chip">
                              <span class="mcl-el-attachment-chip__name">{{ attachment.original_filename }}</span>
                              <span v-if="attachment.file_size" class="mcl-el-attachment-chip__size">
                                {{ formatFileSize(attachment.file_size) }}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </section>
                    </div>

                    <section v-if="log.body_html" class="mcl-el-body-preview">
                      <div class="mcl-el-detail__panel-head">
                        <span class="mcl-el-detail__panel-icon mcl-el-detail__panel-icon--indigo">
                          <font-awesome-icon :icon="['fas', 'envelope-open-text']" />
                        </span>
                        <div>
                          <h4 class="mcl-el-detail__panel-title">Message preview</h4>
                          <p class="mcl-el-detail__panel-desc">Email body content</p>
                        </div>
                      </div>
                      <div class="mcl-el-body-preview__frame">
                        <div class="mcl-el-body-preview__content" v-html="log.body_html"></div>
                      </div>
                    </section>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="paginatorInfo && paginatorInfo.total > 0" class="mcl-el-pagination">
        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../../components/shared/pagination.vue";

export default {
  name: "master-contact-email-logs",
  emits: ["resend"],
  components: {
    Pagination,
  },
  data() {
    return {
      isLoading: false,
      emailLogs: [],
      paginatorInfo: null,
      first: 10,
      page: 1,
      search: "",
      expandedLogId: null,
      activeFolder: "sent",
      logCounts: null,
    };
  },
  computed: {
    loadingMessage: function () {
      if (this.activeFolder === "scheduled") {
        return "Fetching scheduled messages...";
      }
      return "Fetching your sent messages...";
    },
    emptyTitle: function () {
      if (this.search) {
        return "No email logs found";
      }
      if (this.activeFolder === "scheduled") {
        return "No scheduled emails";
      }
      return "No email logs found";
    },
    emptyMessage: function () {
      if (this.search) {
        return "Try adjusting your search keywords.";
      }
      if (this.activeFolder === "scheduled") {
        return "Scheduled emails from this module will appear here.";
      }
      return "Emails sent from this module will appear here once delivered.";
    },
    dateColumnLabel: function () {
      return this.activeFolder === "scheduled" ? "Scheduled for" : "Date";
    },
  },
  mounted() {
    this.fetchLogCounts();
    this.fetchEmailLogs();
  },
  methods: {
    async fetchLogCounts() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query MasterContactEmailLogCounts {
              masterContactEmailLogCounts {
                error
                sent_count
                scheduled_count
              }
            }
          `,
          fetchPolicy: "network-only",
        });
        var payload = data && data.masterContactEmailLogCounts ? data.masterContactEmailLogCounts : null;
        if (payload && !payload.error) {
          this.logCounts = payload;
        }
      } catch (e) {
        console.error(e);
      }
    },
    switchFolder(folder, forceRefresh) {
      if (this.activeFolder === folder && !forceRefresh) {
        return;
      }
      this.activeFolder = folder;
      this.page = 1;
      this.expandedLogId = null;
      this.fetchEmailLogs();
    },
    async fetchEmailLogs() {
      this.isLoading = true;
      this.expandedLogId = null;

      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query MasterContactEmailLogs($first: Int!, $page: Int!, $search: String, $folder: String) {
              masterContactEmailLogs(first: $first, page: $page, search: $search, folder: $folder) {
                error
                error_code
                message
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
                data {
                  id
                  uuid
                  subject
                  status
                  from_email
                  from_name
                  body_html
                  recipient_count
                  attachment_count
                  has_attachments
                  sent_at
                  failed_at
                  last_error
                  created_at
                  owner_name
                  owner_email
                  folder
                  scheduled_at
                  recipients {
                    id
                    recipient_type
                    email
                    name
                    delivery_status
                    delivery_error
                    queued_at
                    delivered_at
                  }
                  attachments {
                    id
                    original_filename
                    mime_type
                    file_size
                    sort_order
                  }
                }
              }
            }
          `,
          variables: {
            first: this.first,
            page: this.page,
            search: this.search ? this.search.trim() : null,
            folder: this.activeFolder,
          },
          fetchPolicy: "network-only",
        });

        const payload = data && data.masterContactEmailLogs ? data.masterContactEmailLogs : null;

        if (payload && payload.error) {
          this.emailLogs = [];
          this.paginatorInfo = null;
          return;
        }

        this.emailLogs = payload && payload.data ? payload.data : [];
        this.paginatorInfo = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
        this.fetchLogCounts();
      } catch (e) {
        console.error(e);
        this.emailLogs = [];
        this.paginatorInfo = null;
      } finally {
        this.isLoading = false;
      }
    },
    refreshLogs() {
      this.fetchLogCounts();
      this.fetchEmailLogs();
    },
    onSearch() {
      this.page = 1;
      this.expandedLogId = null;
      this.fetchEmailLogs();
    },
    setPage(page) {
      this.page = page;
      this.expandedLogId = null;
      this.fetchEmailLogs();
    },
    rowNumber(index) {
      if (this.paginatorInfo && this.paginatorInfo.firstItem) {
        return this.paginatorInfo.firstItem + index;
      }
      return index + 1;
    },
    isExpanded(log) {
      return log && String(log.id) === String(this.expandedLogId);
    },
    toggleExpanded(log) {
      if (!log || !log.id) {
        return;
      }
      if (String(this.expandedLogId) === String(log.id)) {
        this.expandedLogId = null;
      } else {
        this.expandedLogId = log.id;
      }
    },
    resendEmail(log) {
      if (!log) {
        return;
      }
      var draft = this.buildComposeDraft(log);
      draft.resendFromFolder = this.activeFolder;
      this.$emit("resend", draft);
    },
    buildComposeDraft(log) {
      var draft = {
        subject: log.subject ? log.subject : "",
        bodyHtml: log.body_html ? log.body_html : "",
        to: [],
        cc: [],
        bcc: [],
        resendFromFolder: null,
      };
      if (!log.recipients || !log.recipients.length) {
        return draft;
      }
      for (var i = 0; i < log.recipients.length; i++) {
        var recipient = log.recipients[i];
        if (!recipient || !recipient.email) {
          continue;
        }
        var entry = {
          email: recipient.email,
          name: recipient.name ? recipient.name : "",
          id: null,
        };
        var type = recipient.recipient_type;
        if (type === "cc") {
          draft.cc.push(entry);
        } else if (type === "bcc") {
          draft.bcc.push(entry);
        } else {
          draft.to.push(entry);
        }
      }
      return draft;
    },
    recipientCount(log) {
      if (!log) {
        return 0;
      }
      var count = log.recipient_count;
      if (count === undefined || count === null) {
        count = log.recipients && log.recipients.length ? log.recipients.length : 0;
      }
      return parseInt(count, 10) || 0;
    },
    hasSingleRecipient(log) {
      return this.recipientCount(log) === 1;
    },
    singleRecipient(log) {
      if (!this.hasSingleRecipient(log) || !log.recipients || !log.recipients.length) {
        return null;
      }
      return log.recipients[0];
    },
    singleRecipientNamePart(log) {
      var recipient = this.singleRecipient(log);
      if (!recipient) {
        return "—";
      }
      if (recipient.name) {
        return recipient.name.trim();
      }
      return recipient.email || "—";
    },
    singleRecipientEmailPart(log) {
      var recipient = this.singleRecipient(log);
      if (!recipient || !recipient.email) {
        return "";
      }
      if (recipient.name) {
        return recipient.email;
      }
      return "";
    },
    recipientSummary(log) {
      var count = this.recipientCount(log);
      return count + (count === 1 ? " recipient" : " recipients");
    },
    senderLabel(log) {
      if (!log) {
        return "—";
      }
      if (log.owner_name) {
        return log.owner_name;
      }
      if (log.from_name) {
        return log.from_name;
      }
      return "—";
    },
    senderInitials(log) {
      var name = this.senderLabel(log);
      if (!name || name === "—") {
        return "?";
      }
      var parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    },
    fromLabel(log) {
      if (!log) {
        return "—";
      }
      var name = log.from_name || log.owner_name;
      if (name && log.from_email) {
        return name + " <" + log.from_email + ">";
      }
      return log.from_email || "—";
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      var date = new Date(value);
      if (isNaN(date.getTime())) {
        return "—";
      }
      return date.toLocaleString();
    },
    formatDateShort(value) {
      if (!value) {
        return "—";
      }
      var date = new Date(value);
      if (isNaN(date.getTime())) {
        return "—";
      }
      return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    formatTime(value) {
      if (!value) {
        return "";
      }
      var date = new Date(value);
      if (isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatFileSize(bytes) {
      var size = parseInt(bytes, 10);
      if (isNaN(size) || size <= 0) {
        return "0 B";
      }
      if (size < 1024) {
        return size + " B";
      }
      if (size < 1048576) {
        return (size / 1024).toFixed(1) + " KB";
      }
      return (size / 1048576).toFixed(1) + " MB";
    },
    isScheduledLog(log) {
      return log && (log.folder === "scheduled" || this.activeFolder === "scheduled");
    },
    displayDatePrimary(log) {
      if (this.isScheduledLog(log) && log.scheduled_at) {
        return this.formatDateShort(log.scheduled_at);
      }
      return this.formatDateShort(log.sent_at || log.created_at);
    },
    displayDateSecondary(log) {
      if (this.isScheduledLog(log) && log.scheduled_at) {
        return this.formatTime(log.scheduled_at);
      }
      return this.formatTime(log.sent_at || log.created_at);
    },
    statusLabel(status, log) {
      if (log && log.folder === "scheduled") {
        return "Scheduled";
      }
      var labels = {
        draft: "Draft",
        queued: "Queued",
        sending: "Sending",
        sent: "Sent",
        partially_sent: "Partially sent",
        failed: "Failed",
      };
      return labels[status] || status || "—";
    },
    statusClass(status, log) {
      if (log && log.folder === "scheduled") {
        return "mcl-el-status--scheduled";
      }
      if (status === "sent") {
        return "mcl-el-status--sent";
      }
      if (status === "partially_sent") {
        return "mcl-el-status--partial";
      }
      if (status === "failed") {
        return "mcl-el-status--failed";
      }
      if (status === "sending" || status === "queued") {
        return "mcl-el-status--pending";
      }
      return "mcl-el-status--default";
    },
    deliveryStatusLabel(status) {
      var labels = {
        pending: "Pending",
        queued: "Queued",
        sent: "Sent",
        failed: "Failed",
        bounced: "Bounced",
      };
      return labels[status] || status || "—";
    },
    deliveryStatusClass(status) {
      if (status === "sent") {
        return "mcl-el-status--sent";
      }
      if (status === "failed" || status === "bounced") {
        return "mcl-el-status--failed";
      }
      if (status === "queued" || status === "pending") {
        return "mcl-el-status--pending";
      }
      return "mcl-el-status--default";
    },
    recipientTypeLabel(type) {
      if (type === "cc") {
        return "CC";
      }
      if (type === "bcc") {
        return "BCC";
      }
      return "To";
    },
  },
};
</script>

<style scoped>
.mcl-el-page {
  min-height: 100%;
  padding: 1.5rem 1.75rem 2rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.mcl-el-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.mcl-el-tabs__btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.mcl-el-tabs__btn:hover {
  background: #f8fafc;
  color: #334155;
}

.mcl-el-tabs__btn--active {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #0f4c81;
  box-shadow: inset 0 0 0 1px #bfdbfe;
}

.mcl-el-tabs__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  border-radius: 9999px;
  background: #e2e8f0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #475569;
}

.mcl-el-tabs__btn--active .mcl-el-tabs__badge {
  background: #0f4c81;
  color: #ffffff;
}

.mcl-el-tabs__badge--scheduled {
  background: #ffedd5;
  color: #c2410c;
}

.mcl-el-tabs__btn--active .mcl-el-tabs__badge--scheduled {
  background: #ea580c;
  color: #ffffff;
}

.mcl-el-scheduled-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  color: #92400e;
}

.mcl-el-scheduled-banner svg {
  flex-shrink: 0;
  color: #d97706;
}

.mcl-el-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.mcl-el-hero__main {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mcl-el-hero__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: linear-gradient(135deg, #0f4c81 0%, #1d6eb5 100%);
  box-shadow: 0 4px 12px rgba(15, 76, 129, 0.25);
}

.mcl-el-hero__icon {
  font-size: 1.25rem;
  color: #ffffff;
}

.mcl-el-hero__title {
  margin: 0 0 0.2rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.mcl-el-hero__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
}

.mcl-el-hero__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.mcl-el-stat-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.625rem 1rem;
  min-width: 6.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
}

.mcl-el-stat-chip--page {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-color: #e2e8f0;
}

.mcl-el-stat-chip__value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f4c81;
  line-height: 1.2;
}

.mcl-el-stat-chip--page .mcl-el-stat-chip__value {
  color: #334155;
}

.mcl-el-stat-chip__label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-top: 0.125rem;
}

.mcl-el-toolbar-card {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding: 0.875rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.mcl-el-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
  padding: 0.25rem 0.25rem 0.25rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mcl-el-search:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  background: #ffffff;
}

.mcl-el-search__icon {
  flex-shrink: 0;
  color: #94a3b8;
  font-size: 0.875rem;
}

.mcl-el-search__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: #0f172a;
}

.mcl-el-search__input:focus {
  outline: none;
}

.mcl-el-search__input::placeholder {
  color: #94a3b8;
}

.mcl-el-search__btn {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: #0f4c81;
  color: #ffffff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mcl-el-search__btn:hover {
  background: #0c3d66;
}

.mcl-el-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1.125rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  background: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mcl-el-refresh-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f4c81;
}

.mcl-el-refresh-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.mcl-el-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
}

.mcl-el-state__title {
  margin: 0 0 0.375rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
}

.mcl-el-state__text {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  max-width: 22rem;
  line-height: 1.5;
}

.mcl-el-loader {
  position: relative;
  width: 4rem;
  height: 4rem;
  margin-bottom: 1.25rem;
}

.mcl-el-loader__ring {
  position: absolute;
  inset: 0;
  border: 3px solid #e2e8f0;
  border-top-color: #0f4c81;
  border-radius: 50%;
  animation: mcl-el-spin 0.9s linear infinite;
}

.mcl-el-loader__icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.125rem;
  color: #0f4c81;
}

@keyframes mcl-el-spin {
  to {
    transform: rotate(360deg);
  }
}

.mcl-el-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  margin-bottom: 1rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #94a3b8;
  font-size: 1.75rem;
}

.mcl-el-content {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

.mcl-el-table-wrap {
  overflow-x: auto;
}

.mcl-el-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  text-align: left;
}

.mcl-el-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
}

.mcl-el-table th {
  padding: 0.875rem 1rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  white-space: nowrap;
}

.mcl-el-table td {
  padding: 1rem;
  color: #1e293b;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
}

.mcl-el-table__row {
  transition: background 0.12s ease;
}

.mcl-el-table__row:hover {
  background: #f8fafc;
}

.mcl-el-table__row--expanded {
  background: linear-gradient(90deg, rgba(15, 76, 129, 0.06) 0%, rgba(15, 76, 129, 0.02) 100%);
}

.mcl-el-table__row--expanded td {
  border-bottom-color: transparent;
}

.mcl-el-table__col-num {
  width: 3rem;
  text-align: center;
}

.mcl-el-table__col-actions {
  width: 9.5rem;
  text-align: right;
}

.mcl-el-row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
}

.mcl-el-resend-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #ffffff 0%, #eff6ff 100%);
  font-size: 0.75rem;
  font-weight: 600;
  color: #0f4c81;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.mcl-el-resend-btn:hover {
  background: #dbeafe;
  border-color: #93c5fd;
  box-shadow: 0 2px 6px rgba(15, 76, 129, 0.12);
}

.mcl-el-row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  border-radius: 0.375rem;
  background: #f1f5f9;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.mcl-el-table__recipients {
  min-width: 200px;
  max-width: 340px;
}

.mcl-el-recipient-btn {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.12s ease;
}

.mcl-el-recipient-btn:hover {
  opacity: 0.85;
}

.mcl-el-recipient-line__name {
  font-weight: 600;
  color: #0f172a;
}

.mcl-el-recipient-line__email {
  font-weight: 400;
  color: #2563eb;
  font-size: 0.8125rem;
}

.mcl-el-recipient-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1d4ed8;
}

.mcl-el-sender {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.mcl-el-sender__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f4c81 0%, #3b82f6 100%);
  font-size: 0.6875rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
}

.mcl-el-sender__name {
  font-weight: 600;
  color: #334155;
  line-height: 1.3;
}

.mcl-el-subject-cell {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  max-width: 280px;
}

.mcl-el-subject-cell__text {
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mcl-el-subject-cell__attach {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.375rem;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.6875rem;
}

.mcl-el-table__date {
  white-space: nowrap;
}

.mcl-el-date__primary {
  display: block;
  font-weight: 600;
  color: #334155;
  font-size: 0.8125rem;
}

.mcl-el-date__secondary {
  display: block;
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.125rem;
}

.mcl-el-table__date--scheduled .mcl-el-date__primary,
.mcl-el-table__date--scheduled .mcl-el-date__secondary {
  color: #c2410c;
  font-weight: 600;
}

.mcl-el-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.mcl-el-status__dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.7;
}

.mcl-el-status--sm {
  font-size: 0.6875rem;
  padding: 0.2rem 0.5rem;
}

.mcl-el-status--sent {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.mcl-el-status--partial {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.mcl-el-status--failed {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.mcl-el-status--pending {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.mcl-el-status--scheduled {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.mcl-el-status--default {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.mcl-el-expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.2s ease, color 0.15s ease;
}

.mcl-el-expand-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #0f4c81;
}

.mcl-el-expand-btn--open {
  background: #0f4c81;
  border-color: #0f4c81;
  color: #ffffff;
  transform: rotate(180deg);
}

.mcl-el-detail-row td {
  padding: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.mcl-el-detail {
  padding: 1.25rem 1.5rem 1.5rem;
  border-top: 3px solid #0f4c81;
}

.mcl-el-detail__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.mcl-el-detail-resend-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f4c81 0%, #1d6eb5 100%);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 76, 129, 0.22);
  transition: box-shadow 0.15s ease, transform 0.12s ease;
}

.mcl-el-detail-resend-btn:hover {
  box-shadow: 0 6px 16px rgba(15, 76, 129, 0.3);
  transform: translateY(-1px);
}

.mcl-el-detail__toolbar-hint {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.mcl-el-detail__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 900px) {
  .mcl-el-detail__grid {
    grid-template-columns: 1.15fr 0.85fr;
  }
}

.mcl-el-detail__panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.125rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.mcl-el-detail__panel-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid #f1f5f9;
}

.mcl-el-detail__panel-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.mcl-el-detail__panel-icon--blue {
  background: #eff6ff;
  color: #2563eb;
}

.mcl-el-detail__panel-icon--slate {
  background: #f1f5f9;
  color: #475569;
}

.mcl-el-detail__panel-icon--indigo {
  background: #eef2ff;
  color: #4f46e5;
}

.mcl-el-detail__panel-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}

.mcl-el-detail__panel-desc {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #94a3b8;
}

.mcl-el-detail__empty {
  font-size: 0.875rem;
  color: #94a3b8;
  font-style: italic;
}

.mcl-el-recipient-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.mcl-el-recipient-item {
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: border-color 0.12s ease;
}

.mcl-el-recipient-item:hover {
  border-color: #cbd5e1;
}

.mcl-el-recipient-item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}

.mcl-el-recipient-item__type {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  background: #e2e8f0;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
}

.mcl-el-recipient-item__identity {
  margin: 0;
  line-height: 1.4;
}

.mcl-el-recipient-item__name {
  display: block;
  font-weight: 600;
  color: #0f172a;
  font-size: 0.875rem;
}

.mcl-el-recipient-item__email {
  display: block;
  font-size: 0.8125rem;
  color: #2563eb;
  margin-top: 0.125rem;
}

.mcl-el-recipient-item__time {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0.375rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.mcl-el-recipient-item__error {
  margin: 0.5rem 0 0;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  font-size: 0.75rem;
  color: #b91c1c;
  line-height: 1.4;
}

.mcl-el-meta-list {
  margin: 0;
}

.mcl-el-meta-list__item {
  display: grid;
  grid-template-columns: 5rem 1fr;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8125rem;
}

.mcl-el-meta-list__item:last-child {
  border-bottom: none;
}

.mcl-el-meta-list__item dt {
  font-weight: 600;
  color: #94a3b8;
  margin: 0;
}

.mcl-el-meta-list__item dd {
  margin: 0;
  color: #1e293b;
  word-break: break-word;
}

.mcl-el-meta-list__item--error dd {
  color: #b91c1c;
}

.mcl-el-attachments {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.mcl-el-attachments__title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0 0 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #475569;
}

.mcl-el-attachments__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mcl-el-attachment-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.8125rem;
}

.mcl-el-attachment-chip__name {
  font-weight: 500;
  color: #334155;
}

.mcl-el-attachment-chip__size {
  font-size: 0.75rem;
  color: #94a3b8;
}

.mcl-el-body-preview {
  margin-top: 1rem;
  padding: 1.125rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.mcl-el-body-preview__frame {
  margin-top: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  overflow: hidden;
}

.mcl-el-body-preview__content {
  max-height: 220px;
  overflow-y: auto;
  padding: 1rem 1.125rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #334155;
}

.mcl-el-pagination {
  padding: 1rem 1.25rem;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-top: 1px solid #e2e8f0;
}
</style>
